import * as React from 'react';
import {withRouter} from 'react-router-dom';
// @ts-expect-error
import type {ContextRouter} from 'react-router-dom';

import {deserializeUrlParam} from 'nutshell-core';

import {Routing} from 'nutshell-core/routing';
import * as Schema from 'nutshell-core/schema';
import {serialize} from 'nutshell-core/serialize';
import type {EntityListType, FilterObject, UnserializedFilterObject} from 'nutshell-core/types';

import {CloseButton} from 'shells/button/close-button/close-button';

// @ts-expect-error
import {CheckboxFilter} from './checkbox-filter';
import {FilterForm} from './filter-form';
import {FilterSection} from './filter-section';
import {FormMap} from './forms/map';
import {MultipleFilter} from './multiple-filter';

import styles from './filter-section.module.css';

type OwnProps = {
    entityType: EntityListType;
    field: Schema.FieldProperties;
    filter: FilterObject | null | undefined;
    isOpen: boolean;
    allowNegation: boolean;
    onOpenSection: (filterName: string) => any;
    onCloseSection: (filterName: string) => any;
    onSubmitFilter: (filterObj: FilterObject, field: Schema.FieldProperties) => void;
    onRemoveFilter: (filterKey: string) => void;
    shownColumns?: string[];
    updateShownColumns?: (arg1: string[], arg2: boolean) => void;
};

type Props = OwnProps & ContextRouter;

export class FieldFilterComponent extends React.PureComponent<Props> {
    render() {
        const {field, filter, ...restProps} = this.props;

        const value = filter ? deserializeUrlParam(filter[field.name], field.type) : undefined;

        if (field.isFilterModeField || field.isFalseable === false) {
            return (
                <CheckboxFilter
                    name={field.name}
                    label={field.title}
                    value={value}
                    onChange={this.handleChange}
                />
            );
        }

        if (
            field.type === 'enum' ||
            field.type === 'relationship' ||
            field.type === 'stage' ||
            field.type === 'hierarchy'
        ) {
            return (
                <MultipleFilter
                    {...restProps}
                    field={field}
                    filter={filter}
                    shownColumns={this.props.shownColumns}
                    updateShownColumns={this.props.updateShownColumns}
                />
            );
        }

        if (field.type === 'map') {
            return (
                <FilterSection
                    field={field}
                    filter={filter}
                    isOpen={this.props.isOpen}
                    onOpenSection={this.handleMapFilterOpen}
                    onCloseSection={this.handleMapFilterClose}
                    shownColumns={this.props.shownColumns}
                    updateShownColumns={this.props.updateShownColumns}
                >
                    <div className={styles.form}>
                        <FormMap
                            field={this.props.field}
                            filter={this.props.filter}
                            entityType={this.props.entityType}
                        />
                    </div>
                </FilterSection>
            );
        }

        return (
            <FilterSection
                field={field}
                filter={filter}
                isOpen={this.props.isOpen}
                onOpenSection={this.props.onOpenSection}
                onCloseSection={this.props.onCloseSection}
                shownColumns={this.props.shownColumns}
                updateShownColumns={this.props.updateShownColumns}
            >
                <div className={styles.form}>
                    <CloseButton
                        className={styles['close-button']}
                        onClick={this.handleRemoveFilter}
                        size={8}
                    />
                    <FilterForm
                        field={this.props.field}
                        value={value}
                        entityType={this.props.entityType}
                        onChange={this.handleChange}
                        onClearValue={this.handleRemoveFilter}
                    />
                </div>
            </FilterSection>
        );
    }

    handleChange = (data?: UnserializedFilterObject<any> | null) => {
        const field = this.props.field;
        if (!data) {
            this.props.onRemoveFilter(field.name);
        } else {
            const serializedFilter = serialize(data, field.type);
            this.props.onSubmitFilter(serializedFilter, field);
        }
    };

    handleRemoveFilter = () => {
        this.props.onCloseSection(this.props.field.name);
    };

    /*
    If the current view is something other than the map, navigate to the map.
    If they're already on the map, we don't want to allow the filter to be cleared,
    so we do nothing.
     */
    handleMapFilterOpen = () => {
        // TODO (@ianvs): use react-router location here eventually
        const params = Routing.deparam(window.location.search);
        if (params.view === 'map') return;
        const newParams = {...params, view: 'map'} as const;
        this.props.history.push({search: Routing.param(newParams)});
    };

    /*
    If currently on the map, we can't allow clearing the map filter
     */
    handleMapFilterClose = () => {
        // TODO (@ianvs): use react-router location here eventually
        const params = Routing.deparam(window.location.search);
        if (params.view === 'map') return;

        this.props.onCloseSection(this.props.field.name);
    };
}

// @ts-expect-error
export const FieldFilter = withRouter<OwnProps>(FieldFilterComponent);
